import React, { useRef, useState } from 'react';
import { styled } from 'linaria/react';
import { theme } from '../../Theming/Theming';
import t from '@jetshop/intl';
import addMultipleToCartMutation from '../../Cart/queries/addMultipleToCart.gql';
import cartQuery from '../../Cart/queries/cartQuery.gql';
import { ReactComponent as Cart } from '../../../svg/Cart.svg';
import { useAddMultipleToCart } from '@jetshop/core/hooks/useAddMultipleToCart';
import { useNotification } from '@jetshop/core/components/Notifications';
import { ButtonUI } from '../../ui/ButtonUI';
import PlusIcon from '../../ui/icons/PlusIcon';
import MinusIcon from '../../ui/icons/MinusIcon';
import ReadMore from '../../Util/ReadMore';

const Wrapper = styled.div`
  .read-more-wrapper {
    padding-bottom: 1rem;
    .read-more-button {
      bottom: 5px;
      .icon {
        bottom: 5px;
      }
    }
  }
`;

const GridWrapper = styled.div`
  margin: 1rem 0;
  width: 100%;
`;
const VariantWrapper = styled.div`
  padding: 2px 0;
  height: 28px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  border-bottom: 1px solid #eee;
  &:first-child {
    border-top: 1px solid #eee;
  }
  &.not-buyable {
    color: ${theme.colors.outOfStock};
  }
  &.selected {
    background: #f9f9f9;
    .variant-info {
      font-weight: ${theme.weights.bold};
    }
  }
  input {
    font-size: 0.85rem;
  }
  .variant-info {
    cursor: pointer;
    display: flex;
    align-items: center;

    > div {
      display: flex;
      align-items: center;
    }

    .color-circle {
      margin-right: 5px;
    }

    .variant-two,
    .stock-status {
      &:before {
        content: '-';
        margin: 0 5px;
      }
    }
  }
  input {
    font-size: 0.8rem;
    border: none;
    border-top: 1px solid #ddd;
    border-bottom: 1px solid #ddd;
    width: 40px;
    height: 24px;
    padding: 0 5px;
  }
`;

const InputControl = styled.div`
  display: flex;
  button {
    background: #f2f2f2;
    height: 24px;
    width: 20px;
    border: 1px solid #ddd;
    display: flex;
    justify-content: center;
    align-items: center;
    cursor: pointer;
    &:focus {
      border-color: ${theme.colors.black};
    }
    svg {
      font-size: 0.7rem;
      color: #555;
    }
    &:hover svg {
      color: #000;
    }
  }
`;

const ButtonWrapper = styled.div`
  width: 100%;
  button {
    width: 100%;
  }
`;

const AllToaster = styled.div`
  background: white;
  color: ${theme.colors.black};
  padding: 1em;
  display: flex;
  align-items: center;

  svg {
    max-width: 1.5em;
    max-height: 1.5em;
    margin-right: 0.5em;
  }
`;

const Variant = ({ product, updateList, variant, variantHandler }) => {
  const [quantity, setQuantity] = useState(0);
  const inputRef = useRef(null);
  const handleChange = (variant, quantity) => {
    if (quantity < 0) return false;
    const quantityInt = isNaN(quantity) ? 0 : parseInt(quantity);
    if (quantityInt < 0) return false;
    setQuantity(quantityInt);
    updateList(variant, quantityInt);
  };
  const buyable = variant.stockStatus.buyable;
  return (
    <VariantWrapper
      className={`${
        variantHandler.selectedVariant === variant ? 'selected' : ''
      }${variant.stockStatus.buyable ? ' buyable' : ' not-buyable'}`}
      onClick={() => {
        variantHandler.selectValue(
          variant.values[0],
          product.variants.options[0]
        );
        if (variant.values.length > 1)
          variantHandler.selectValue(
            variant.values[1],
            product.variants.options[1]
          );
      }}
    >
      <div className="variant-info">
        <div className="variant-one">
          <div>{variant.values[0]}</div>
        </div>
        {variant.values.length > 1 && (
          <div className="variant-two">{variant.values[1]}</div>
        )}
        {!buyable && (
          <span className="stock-status">{variant.stockStatus.text}</span>
        )}
      </div>
      {buyable && (
        <InputControl>
          <button
            className="minus"
            onClick={() => {
              handleChange(variant, quantity - 1);
            }}
          >
            <MinusIcon />
          </button>
          <input
            ref={inputRef}
            onChange={(e) => {
              handleChange(variant, parseInt(e.target.value, 10));
            }}
            type="number"
            value={quantity}
            min="0"
          />
          <button
            className="plus"
            onClick={() => {
              handleChange(variant, quantity + 1);
            }}
          >
            <PlusIcon />
          </button>
        </InputControl>
      )}
    </VariantWrapper>
  );
};

export const VariantMultiBuy = ({
  product,
  variantHandler,
  close = () => {},
  validationMessage,
  enableValidation,
}) => {
  //VARIANT LIST
  const [variantList, setList] = useState([]);
  const enableBuy = variantList.length > 0;

  //NOTIFICATION ON ADD TO CART
  const [trigger] = useNotification();

  //MULTIPLE ADD TO CART
  const productCount = variantList.length;
  const [addToCart, { loading: mutationLoading }] = useAddMultipleToCart(
    variantList,
    {
      addMultipleToCartMutation,
      cartQuery,
    },
    {
      onCompleted: () => {
        trigger(
          <AllToaster>
            <Cart />
            {t(
              '{productCount, plural, =1 {# variant added to the cart.} other {# variants added to the cart.}}',
              { productCount: productCount }
            )}
          </AllToaster>
        );
      },
    }
  );

  if (
    !product.variants ||
    !product.variants.values ||
    product.variants.values.length === 0
  )
    return false;

  const variants = true
    ? [...product.variants.values].sort((a, b) =>
        a.values[1] > b.values[1] ? -1 : 1
      )
    : product.variants.values;

  function updateList(variant, quantity) {
    let hit = false;
    const newList = variantList.filter((item) => {
      if (item.articleNumber === variant.articleNumber) {
        hit = true;
        item.quantity = quantity;
        return quantity > 0;
      }
      return true;
    });
    if (hit) setList(newList);
    else setList([...variantList, { ...variant, quantity }]);
  }

  return (
    <Wrapper>
      <ReadMore breakPoint={400} maxHeight={300} icon={true}>
        <GridWrapper>
          {variants.map((variant, index) => (
            <Variant
              key={index}
              {...{ product, variant, updateList, variantHandler }}
            />
          ))}
        </GridWrapper>
      </ReadMore>
      <ButtonWrapper>
        <ButtonUI
          css={{ marginTop: '1rem' }}
          className={`buy same`}
          disabled={!enableBuy || mutationLoading}
          onClick={() => {
            if (variantHandler.validation === 'valid') {
              addToCart();
              close();
            } else enableValidation();
          }}
        >
          {mutationLoading
            ? t('Adding to cart')
            : enableBuy
            ? t('Add to cart')
            : t('Choose variants')}
        </ButtonUI>
      </ButtonWrapper>
    </Wrapper>
  );
};
